(() => {
  if (window.AvonticPerformance) return;

  const loadedScripts = new Map();
  const deferredSelector = '[data-av-deferred-recommendations], [data-av-deferred-recently-viewed]';
  const recommendationRootSelector = '[data-av-recommendation-engine]';
  const deferredMediaSelector = '.av-ph__video, video[data-av-defer-media]';
  const deviceTriggerSelector = [
    '[data-fit-open]',
    '[data-avontic-profile-link]',
    '[data-fit-search]',
    '[data-fit-family]',
    '[data-fit-model]',
    '[data-fit-spec]',
    'a[href*="/pages/my-devices"]',
    'a[href*="/pages/protection-planner"]',
    'a[href*="/pages/compare-products"]'
  ].join(',');
  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const constrainedConnection = Boolean(connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || ''));
  const commerceRootMargin = constrainedConnection ? '120px 0px' : '480px 0px';
  const mediaRootMargin = constrainedConnection ? '0px' : '320px 0px';
  // Keep the third-party OpenAI pixel dormant until the published privacy policy names the processor.
  const openAIPixelEnabled = false;
  let openAIPixelLoaded = false;
  let openAIPixelScheduled = false;
  let compatibilityPromise;

  const scheduleIdle = (callback, timeout = 3000) => {
    if ('requestIdleCallback' in window) return window.requestIdleCallback(callback, { timeout });
    return window.setTimeout(callback, Math.min(timeout, 800));
  };

  const onReady = (callback) => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', callback, { once: true });
    else callback();
  };

  const sourceFor = (name) => document.querySelector(`meta[name="${name}"]`)?.content || '';

  const loadScript = (source) => {
    if (!source) return Promise.resolve();
    const absoluteSource = new URL(source, window.location.href).href;
    if (loadedScripts.has(absoluteSource)) return loadedScripts.get(absoluteSource);
    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = absoluteSource;
      script.async = true;
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', () => {
        loadedScripts.delete(absoluteSource);
        reject(new Error(`Unable to load ${absoluteSource}`));
      }, { once: true });
      document.head.append(script);
    });
    loadedScripts.set(absoluteSource, promise);
    return promise;
  };

  const loadRecommendationEngine = () => loadScript(sourceFor('avontic-recommendation-engine')).catch(() => {});

  const loadCompatibilityRuntime = () => {
    if (window.AvonticDeviceProfile && window.AvonticCompatibility) return Promise.resolve();
    if (compatibilityPromise) return compatibilityPromise;
    compatibilityPromise = loadScript(sourceFor('avontic-device-compatibility-core'))
      .then(() => loadScript(sourceFor('avontic-device-compatibility-fixes')))
      .catch(() => {
        compatibilityPromise = undefined;
      });
    return compatibilityPromise;
  };

  const normalizeNonBlockingRecommendationActions = (scope = document) => {
    const cards = [];
    if (scope instanceof Element && scope.matches('[data-av-recommendation]')) cards.push(scope);
    scope.querySelectorAll?.('[data-av-recommendation]').forEach((card) => cards.push(card));
    cards.forEach((card) => {
      if (card.dataset.available === 'false') return;
      const primary = card.querySelector('[data-av-primary-action]');
      const blocked = card.querySelector('[data-av-source-blocked-action]');
      const review = card.querySelector('[data-av-review-action]');
      if (!review || !((primary && primary.disabled) || (blocked && !blocked.hidden))) return;
      if (primary) primary.hidden = true;
      if (blocked) blocked.hidden = true;
      review.hidden = false;
      review.removeAttribute('aria-disabled');
      const label = review.querySelector('span');
      if (label) label.textContent = card.closest(recommendationRootSelector)?.dataset.labelChoose || 'Choose options';
    });
  };

  const activateDeferredCommerce = (placeholder) => {
    if (!(placeholder instanceof HTMLElement) || placeholder.dataset.avActivated === 'true') return;
    placeholder.dataset.avActivated = 'true';
    const isRecent = placeholder.hasAttribute('data-av-deferred-recently-viewed');
    const element = document.createElement(isRecent ? 'recently-viewed-products' : 'product-recommendations');
    [...placeholder.attributes].forEach(({ name, value }) => {
      if (!name.startsWith('data-av-deferred') && name !== 'data-av-activated') element.setAttribute(name, value);
    });
    while (placeholder.firstChild) element.append(placeholder.firstChild);
    placeholder.replaceWith(element);
  };

  const commerceObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          commerceObserver.unobserve(entry.target);
          if (entry.target.matches(deferredSelector)) activateDeferredCommerce(entry.target);
          if (entry.target.matches(recommendationRootSelector)) {
            normalizeNonBlockingRecommendationActions(entry.target);
            loadRecommendationEngine();
          }
        });
      }, { rootMargin: commerceRootMargin })
    : null;

  const hydrateDeferredMedia = (video) => {
    if (!(video instanceof HTMLVideoElement) || video.dataset.avMediaHydrated === 'true') return;
    video.dataset.avMediaHydrated = 'true';
    video.preload = video.dataset.avOriginalPreload || 'metadata';
    video.load();
  };

  const mediaObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          mediaObserver.unobserve(entry.target);
          hydrateDeferredMedia(entry.target);
        });
      }, { rootMargin: mediaRootMargin })
    : null;

  const applyReducedMotion = (scope = document) => {
    if (!reducedMotion?.matches) return;
    const videos = [];
    if (scope instanceof HTMLVideoElement && scope.matches('video[autoplay]')) videos.push(scope);
    scope.querySelectorAll?.('video[autoplay]').forEach((video) => videos.push(video));
    videos.forEach((video) => {
      video.autoplay = false;
      video.removeAttribute('autoplay');
      video.pause();
    });
  };

  const prepareDeferredMedia = (scope = document) => {
    const videos = [];
    if (scope instanceof HTMLVideoElement && scope.matches(deferredMediaSelector)) videos.push(scope);
    scope.querySelectorAll?.(deferredMediaSelector).forEach((video) => videos.push(video));
    videos.forEach((video) => {
      if (video.dataset.avMediaObserved === 'true') return;
      video.dataset.avMediaObserved = 'true';
      video.dataset.avOriginalPreload = video.getAttribute('preload') || 'metadata';
      video.preload = 'none';
      if (mediaObserver) mediaObserver.observe(video);
      else scheduleIdle(() => hydrateDeferredMedia(video), constrainedConnection ? 7000 : 1800);
    });
    applyReducedMotion(scope);
  };

  const observe = (scope = document) => {
    const targets = [];
    if (scope instanceof Element && scope.matches(`${deferredSelector}, ${recommendationRootSelector}`)) targets.push(scope);
    scope.querySelectorAll?.(`${deferredSelector}, ${recommendationRootSelector}`).forEach((target) => targets.push(target));
    targets.forEach((target) => {
      if (target.dataset.avPerformanceObserved === 'true') return;
      target.dataset.avPerformanceObserved = 'true';
      if (commerceObserver) commerceObserver.observe(target);
      else scheduleIdle(() => {
        if (target.matches(deferredSelector)) activateDeferredCommerce(target);
        else {
          normalizeNonBlockingRecommendationActions(target);
          loadRecommendationEngine();
        }
      }, constrainedConnection ? 7000 : 1800);
    });
    prepareDeferredMedia(scope);
  };

  const labelVideoFallbacks = (root = document) => {
    root.querySelectorAll?.('video[aria-label] > img:not([alt])').forEach((image) => {
      image.alt = image.parentElement.getAttribute('aria-label') || '';
    });
  };

  const repairLegacyPrivacyLinks = (root = document) => {
    const links = root.querySelectorAll?.('#shopify-pc__banner__body-policy-link, a[href*="avontic.com/policies/privacy-policy"]') || [];
    links.forEach((link) => link.setAttribute('href', '/policies/privacy-policy'));
    return links.length > 0;
  };

  const schedulePrivacyRepairs = () => {
    [0, 250, 1000, 3000, 8000].forEach((delay) => window.setTimeout(repairLegacyPrivacyLinks, delay));
  };

  const loadOpenAIPixel = () => {
    if (!openAIPixelEnabled) return false;
    if (openAIPixelLoaded) return true;
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function' || !privacy.analyticsProcessingAllowed()) return false;
    openAIPixelLoaded = true;
    if (!window.oaiq) {
      const queue = (...args) => queue.q.push(args);
      queue.q = [];
      window.oaiq = queue;
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';
      document.head.append(script);
    }
    window.oaiq('init', { pixelId: 'Ua2eEqgGxc4jzVxZ5ZCmR5', debug: false });
    return true;
  };

  const scheduleOpenAIPixel = () => {
    if (!openAIPixelEnabled) return;
    if (openAIPixelLoaded || openAIPixelScheduled) return;
    openAIPixelScheduled = true;
    const queue = () => {
      openAIPixelScheduled = false;
      scheduleIdle(loadOpenAIPixel, 5000);
    };
    if (document.readyState === 'complete') queue();
    else window.addEventListener('load', queue, { once: true });
  };

  const interactionTarget = (event) => event.target instanceof Element ? event.target : event.target?.parentElement;

  document.addEventListener('pointerdown', (event) => {
    const target = interactionTarget(event);
    const recommendation = target?.closest(recommendationRootSelector);
    if (recommendation) {
      normalizeNonBlockingRecommendationActions(recommendation);
      loadRecommendationEngine();
    }
    if (target?.closest(deviceTriggerSelector)) loadCompatibilityRuntime();
  }, { capture: true, passive: true });

  document.addEventListener('focusin', (event) => {
    const target = interactionTarget(event);
    const recommendation = target?.closest(recommendationRootSelector);
    if (recommendation) {
      normalizeNonBlockingRecommendationActions(recommendation);
      loadRecommendationEngine();
    }
    if (target?.closest(deviceTriggerSelector)) loadCompatibilityRuntime();
  });

  document.addEventListener('visitorConsentCollected', () => {
    scheduleOpenAIPixel();
    schedulePrivacyRepairs();
  });

  document.addEventListener('shopify:section:load', (event) => {
    observe(event.target);
    labelVideoFallbacks(event.target);
    repairLegacyPrivacyLinks(event.target);
  });

  reducedMotion?.addEventListener?.('change', () => applyReducedMotion());

  onReady(() => {
    observe();
    labelVideoFallbacks();
    schedulePrivacyRepairs();
  });

  scheduleOpenAIPixel();
  window.setTimeout(scheduleOpenAIPixel, 3500);
  window.setTimeout(scheduleOpenAIPixel, 8000);

  if (sourceFor('avontic-device-compatibility-core') && !constrainedConnection) {
    const queueCompatibility = () => scheduleIdle(loadCompatibilityRuntime, 9000);
    if (document.readyState === 'complete') queueCompatibility();
    else window.addEventListener('load', queueCompatibility, { once: true });
  }

  window.AvonticPerformance = Object.freeze({
    observe,
    activateDeferredCommerce,
    loadRecommendationEngine,
    loadCompatibilityRuntime,
    normalizeNonBlockingRecommendationActions,
    prepareDeferredMedia
  });
})();
